import { Trash } from "lucide-react";
import { useTranslation } from "react-i18next";
import ImageOrVideo from "../tool_components/ImageOrVideo";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { CourseData } from "@packages/constant/CourseData";

interface CardItem {
  id: string;
  imageUrl: string;
  videoUrl?: string;
  title?: string;
  prompt: string;
  description: string;
  courseName: string;
  gradeLevel: number;
  createdAt: string;
}

interface CardProps {
  item: CardItem;
  selectItem: (item: CardItem) => void;
  setShowDeletePopup?: (id: string) => void;
  customPreview?: React.ReactNode;
}

const Card = ({
  item,
  selectItem,
  setShowDeletePopup,
  customPreview,
}: CardProps) => {
  const { t } = useTranslation();

  return (
    <div
      key={item.id}
      onClick={() => selectItem(item)}
      data-testid="tool-card"
      className="group cursor-pointer bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
    >
      <div className="relative h-48">
        {setShowDeletePopup && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeletePopup(item.id);
            }}
            variant="solid"
            size="icon"
            rounded="full"
            data-testid="delete-tool-button"
            bgColor="bg-white/90 dark:bg-gray-900/90 hover:bg-red-200 dark:hover:bg-red-900"
            borderWidth="thin"
            borderColor="border-gray-200/50 dark:border-gray-700/50"
            className="absolute top-2 right-2 z-10 opacity-100 group-hover:opacity-100 md:opacity-0 shadow-sm"
          >
            <Trash
              size={16}
              className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-500 transition-colors"
            />
          </Button>
        )}
        {customPreview && !item.imageUrl ? (
          customPreview
        ) : (
          <div className="w-full aspect-[16/9] bg-gray-200 dark:bg-gray-700 rounded-t-lg">
            <ImageOrVideo
              imageUrl={item.imageUrl}
              videoUrl={item.videoUrl}
              topic={item.title ?? item.prompt}
              toolColor={(() => {
                const colorMap = {
                  HISTORY: "amber",
                  TURKISH: "blue",
                  SCIENCE: "green",
                  GEOGRAPHY: "orange",
                  CHEMISTRY: "purple",
                  PHYSICS: "red",
                  MATHEMATICS: "indigo",
                  BIOLOGY: "emerald",
                  DIGITAL_CITIZENSHIP: "sky",
                  PHILOSOPHY: "rose",
                  DIN_KULTURU: "yellow",
                  ENGLISH: "blue",
                  AP_ASTRONOMY: "indigo",
                  AP_PSYCHOLOGY: "pink",
                  AP_BUSINESS: "emerald",
                  AP_ECONOMICS: "amber",
                  AP_PHYSICS: "red",
                  AP_SOCIOLOGY: "purple",
                  AP_WORLD_HISTORY_1: "orange",
                  AP_WORLD_HISTORY_2: "orange",
                  AP_BIOLOGY: "emerald",
                  AP_CHEMISTRY: "purple",
                  UNKNOWN: "blue",
                };
                return (
                  colorMap[item.courseName as keyof typeof colorMap] || "blue"
                );
              })()}
              isLoading={false}
              resolution="high"
              createdAt={item.createdAt}
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center space-x-2 mb-2">
              {!!item.courseName && (
                <Text
                  variant="span"
                  size={"xs"}
                  weight={"medium"}
                  className="px-2 py-1 bg-blue-500/90 text-white rounded-full"
                >
                  {t(
                    `courseType.${CourseData[
                      item.courseName as keyof typeof CourseData
                    ]?.name(item.gradeLevel ?? 8)}`,
                  )}
                </Text>
              )}
              {!!item.gradeLevel && (
                <Text
                  variant="span"
                  size={"xs"}
                  weight={"medium"}
                  className="px-2 py-1 bg-purple-500/90 text-white rounded-full"
                >
                  {t("hooks.form.gradeOf", {
                    grade: item.gradeLevel,
                  })}
                </Text>
              )}
            </div>
            <Text variant={"h3"} className="!text-white">
              {item.title ?? item.prompt}
            </Text>
            {!!item.description ||
              (item.description !== "None" && (
                <div className="bg-gradient-to-t from-black to-transparent p-3 rounded -mx-4 -mb-4">
                  <p className="text-sm text-gray-200 line-clamp-2">
                    {item.description}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;
